import * as React from "react";
import { Toast } from "react-ui";
import * as ReactDOM from "react-dom";
import { ThemeProvider, useTheme } from "@emotion/react";

import { IForm } from "../../../Engine/Form/IForm";
import { updateDebugTheme } from "../../../Forms/CandyThemeWrapper";
import { getDraftFullId } from "../../../Forms/Types/FormSettings";
import { setPanelActiveTab, setPanelState } from "../../../Engine/ActionCreators/HiddenPanelActionCreators";
import { getErrorLog } from "../../../Engine/ActionCreators/ErrorLogActionCreators";
import { FormDebugInfoModal } from "../FormDebugInfoModal/FormDebugInfoModal";
import { FakeErrorLogsSetter } from "../DebugTools/FakeErrorLogSetter/FakeErrorLogSetter";
import { SearchPathModal } from "../FormDebugInfoModal/SearchPathModal";
import { SetInnerModal } from "../DebugTools/SetInnerModal/SetInnerModal";
import { SetFakeRequisitesModal } from "../DebugTools/SetFakeRequisitesModal/SetFakeRequisitesModal";
import { DebugInfoEnabledContext } from "../../DebugInfoEnabledContext";
import { isScreenshotTestingMode, isTestingMode } from "../../Testing";
import { useKeyboardShortcut } from "../../Commons/Keyboard/UseKeyboardShortcut";
import { Action } from "../../../../../Common/Funcs";
import { useLocalStorage } from "../../../../../Common/Hooks/UseLocalStorage";
import { FormObjectProvider } from "../../FormObjectProvider";
import { ThemeVariant } from "../ThemeProvider/ThemeProvider";

import { DebugToolsHint } from "./DebugToolsHint/DebugToolsHint";
import { toggleBoolParamInUrl } from "./ToggleBoolParamInUrl";
import { RulerGrid } from "./RulerGrid/RulerGrid";

export interface FormDebugToolsProps {
    form: IForm;
    children: React.ReactNode;
    onSendForm: Action<void>;
    isReadOnly?: boolean;
    isIndividual?: boolean;
    isDisabledCleanForm?: boolean;
    isDebug?: boolean;
}

export function FormDebugTools(props: FormDebugToolsProps): React.ReactElement {
    const form = props.form;
    const [showDebugInfo, setShowDebugInfo] = useLocalStorage("showDebugInfo", false);
    const debugModalNode = React.useRef<HTMLDivElement | undefined>();
    const theme = useTheme();

    const toggleDebugMode = async (): Promise<void> => {
        Toast.push("Переключение debug=true. Переход произоёдет сразу после сохранения данных...", {
            label: "Открыть сразу в новой вклдаке",
            handler: () => {
                toggleBoolParamInUrl("debug");
            },
        });
        await form.changeSet.send();
        toggleBoolParamInUrl("debug");
    };

    const toggleReadonlyMode = async (): Promise<void> => {
        Toast.push("Переключение режима ReadOnly");
        await form.changeSet.send();
        toggleBoolParamInUrl("isReadOnly");
    };

    const toggleDisabledClean = async (): Promise<void> => {
        Toast.push("Переключение режима isDisabledCleanForm");
        await form.changeSet.send();
        toggleBoolParamInUrl("isDisabledCleanForm");
    };

    const toggleIndividual = async (): Promise<void> => {
        Toast.push("Переключение режима isIndividual");
        await form.changeSet.send();
        toggleBoolParamInUrl("isIndividual");
    };

    const toggleScenario = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const param = urlParams.get("scenario");
        switch (param) {
            case "":
                urlParams.set("scenario", "elba");
                break;
            case "elba":
                urlParams.set("scenario", "evrika");
                break;
            case "evrika":
                urlParams.delete("scenario");
                break;
            default:
                urlParams.set("scenario", "elba");
                break;
        }
        window.location.search = urlParams.toString();
    };

    const handleToggleDebugMode = async () => {
        await toggleDebugMode();
    };
    const handleToggleReadonlyMode = async () => {
        await toggleReadonlyMode();
    };

    const refreshWithNormalization = async () => {
        form.setViewModelField(form.settings.formRoot, "resourcesHash", Date.now().toString());
        await form.changeSet.send();
        window.location.reload();
    };

    const handleToggleTheme = (candyTheme: ThemeVariant) => {
        updateDebugTheme(x => (x === undefined || x !== candyTheme ? candyTheme : undefined));
    };

    useKeyboardShortcut("Ctrl+Alt+D", toggleDebugMode);

    const showFormDebugInfo = (): void => {
        const debugNode = getNodeForDebugElements();
        ReactDOM.render(
            <FormObjectProvider form={form}>
                <ThemeProvider theme={theme}>
                    <FormDebugInfoModal form={props.form} onClose={handleCloseDebugModal} />
                </ThemeProvider>
            </FormObjectProvider>,
            debugNode
        );
    };

    const handleDownloadFuf = async (): Promise<void> => {
        Toast.push("Сохраняем данные...");
        await form.changeSet.send();
        await form.normalizeDirtyMasksAndReloadInnerIfRequired();
        await form.candyApiDraftClient.downloadFuf();
    };

    const showFakeErrorLogsSetter = (): void => {
        const debugNode = getNodeForDebugElements();
        ReactDOM.render(
            <ThemeProvider theme={theme}>
                <FakeErrorLogsSetter form={form} onErrorCheck={handleRunErrorCheck} onClose={handleCloseDebugModal} />
            </ThemeProvider>,
            debugNode
        );
    };

    const showSearchPathModal = (): void => {
        if (process.env.NODE_ENV === "development") {
            const debugNode = getNodeForDebugElements();
            ReactDOM.render(
                <ThemeProvider theme={theme}>
                    <SearchPathModal form={form} onClose={handleCloseDebugModal} />
                </ThemeProvider>,
                debugNode
            );
        }
    };

    const showSetInnerModal = (): void => {
        if (process.env.NODE_ENV === "development") {
            const debugNode = getNodeForDebugElements();
            ReactDOM.render(
                <ThemeProvider theme={theme}>
                    <SetInnerModal candyApiDraftClient={form.candyApiDraftClient} onClose={handleCloseDebugModal} />
                </ThemeProvider>,
                debugNode
            );
        }
    };

    const showSetFakeRequisitesModal = (): void => {
        if (process.env.NODE_ENV === "development") {
            const debugNode = getNodeForDebugElements();
            ReactDOM.render(
                <ThemeProvider theme={theme}>
                    <SetFakeRequisitesModal
                        onClose={handleCloseDebugModal}
                        requisiteList={form.requisiteList}
                        requisitesInfo={form.requisites}
                        draftId={getDraftFullId(form.settings)}
                    />
                </ThemeProvider>,
                debugNode
            );
        }
    };

    const getNodeForDebugElements = (): HTMLElement => {
        if (debugModalNode.current == undefined) {
            debugModalNode.current = window.document.createElement("div");
            window.document.body.appendChild(debugModalNode.current);
        }
        return debugModalNode.current;
    };

    const handleCloseDebugModal = () => {
        if (debugModalNode.current != undefined) {
            ReactDOM.unmountComponentAtNode(debugModalNode.current);
        }
    };

    const handleRunErrorCheck = async () => {
        form.store.dispatch(setPanelState("pin"));
        form.store.dispatch(setPanelActiveTab("check"));
        await form.store.dispatch(getErrorLog);
    };

    return (
        <DebugInfoEnabledContext.Provider value={showDebugInfo}>
            <RulerGrid>{props.children}</RulerGrid>
            <DebugToolsHint
                onDownloadFuf={handleDownloadFuf}
                onSendForm={props.onSendForm}
                candyApiDraftClient={form.candyApiDraftClient}
                onToggleDebugMode={handleToggleDebugMode}
                onToggleReadonlyMode={handleToggleReadonlyMode}
                showDebugToolsHint={(form.settings.debug ?? false) && !isTestingMode() && !isScreenshotTestingMode()}
                formDebugMode={form.settings.debug ?? false}
                onRefreshWithNormalization={refreshWithNormalization}
                onShowControlDebugInfo={() => {
                    setShowDebugInfo(!showDebugInfo);
                }}
                onShowSearchPathModal={showSearchPathModal}
                onShowFormDebugInfo={showFormDebugInfo}
                onShowFakeErrorLogsSetter={showFakeErrorLogsSetter}
                onShowShowSetInnerModal={showSetInnerModal}
                onSetFakeRequisites={showSetFakeRequisitesModal}
                onToggleTheme={candyTheme => handleToggleTheme(candyTheme)}
                onToggleDisabledClean={toggleDisabledClean}
                onToggleIndividual={toggleIndividual}
                onToggleScenario={toggleScenario}
                isReadOnly={props.isReadOnly}
                isIndividual={props.isIndividual}
                isDisabledCleanForm={props.isDisabledCleanForm}
                isDebug={props.isDebug}
            />
        </DebugInfoEnabledContext.Provider>
    );
}
